import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { trackReactionsApi } from '../../api/trackReactions';
import { subscribeToBattle } from '../../api/websocket';
import { UserContext } from '../../context/UserContext';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/components/TrackReactions.css';

const QUICK_EMOJIS = ['🔥', '👏', '💯', '🤯', '💪', '🎯'];

/**
 * Emoji-реакции зрителей на трек в раунде баттла.
 *
 * Зритель может:
 * - поставить реакцию через пикер (+ 😊);
 * - кликнуть по чипу чужой реакции — добавит такую же;
 * - кликнуть по своей реакции — снимет её.
 *
 * Собственные реакции определяются по profileId из списка реакций
 * (PublicProfile.id совпадает с currentUser.profiles[].id / publicProfile.id).
 *
 * @component
 * @param {Object} props
 * @param {string} props.battleId — UUID баттла
 * @param {string} props.roundId — UUID раунда
 * @param {string} props.trackId — UUID трека
 * @param {boolean} [props.compact=false] — Компактный режим (без счётчика «Всего»)
 */
export default function TrackReactions({ battleId, roundId, trackId, compact = false }) {
    const { currentUser } = useContext(UserContext);
    const [reactions, setReactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null);
    const [error, setError] = useState(null);
    const [showPicker, setShowPicker] = useState(false);

    /** Загрузка списка реакций (статистику считаем на клиенте) */
    const load = useCallback(async () => {
        if (!battleId || !roundId || !trackId) return;
        try {
            const data = await trackReactionsApi.list(battleId, roundId, trackId);
            setReactions(data || []);
            setError(null);
        } catch (err) {
            console.error('[TrackReactions] Failed to load reactions:', err.message);
            setError('Не удалось загрузить реакции');
        } finally {
            setLoading(false);
        }
    }, [battleId, roundId, trackId]);

    useEffect(() => {
        load();
    }, [load]);

    /** Live-обновление: REST-события реакций в /topic/battle.{battleId} */
    useEffect(() => {
        if (!battleId || !trackId) return undefined;
        const subscription = subscribeToBattle(battleId, (event) => {
            if (!event || !['REACTION_ADDED', 'REACTION_REMOVED'].includes(event.type)) return;
            if (event.trackId && event.trackId !== trackId) return;
            load();
        });
        return () => subscription?.unsubscribe?.();
    }, [battleId, trackId, load]);

    /** ID профилей текущего пользователя (publicProfile.id, profiles[].id, id) */
    const myProfileIds = useMemo(() => {
        if (!currentUser) return new Set();
        const ids = new Set();
        if (currentUser.publicProfile?.id) ids.add(currentUser.publicProfile.id);
        if (Array.isArray(currentUser.profiles)) {
            currentUser.profiles.forEach(p => { if (p?.id) ids.add(p.id); });
        }
        if (currentUser.id) ids.add(currentUser.id);
        return ids;
    }, [currentUser]);

    /** Статистика по emoji + какие emoji поставил текущий пользователь */
    const { byEmoji, myEmojis, totalCount } = useMemo(() => {
        const counts = {};
        const mine = new Set();
        (reactions || []).forEach(r => {
            counts[r.emoji] = (counts[r.emoji] || 0) + 1;
            if (myProfileIds.has(r.profileId)) mine.add(r.emoji);
        });
        return { byEmoji: counts, myEmojis: mine, totalCount: (reactions || []).length };
    }, [reactions, myProfileIds]);

    const emojiList = Object.keys(byEmoji);

    /** Добавить/снять реакцию кликом по чипу или элементу пикера */
    const handleToggle = async (emoji) => {
        try {
            setActionLoading(emoji);
            if (myEmojis.has(emoji)) {
                await trackReactionsApi.removeOne(battleId, roundId, trackId, emoji);
            } else {
                await trackReactionsApi.add(battleId, roundId, trackId, { emoji });
            }
            await load();
        } catch (err) {
            console.error('[TrackReactions] Failed to toggle reaction:', err.message);
            setError('Не удалось поставить реакцию');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return <HrnbLoader size="sm" />;
    }

    return (
        <div className={`track-reactions${compact ? ' track-reactions--compact' : ''}`}>
            {error && (
                <div className="track-reactions__error">
                    <small>{error}</small>
                </div>
            )}

            <div className="track-reactions__list">
                {emojiList.map((emoji) => (
                    <button
                        key={emoji}
                        type="button"
                        className={`reaction-chip reaction-chip--interactive${myEmojis.has(emoji) ? ' reaction-chip--mine' : ''}`}
                        onClick={() => handleToggle(emoji)}
                        disabled={actionLoading !== null}
                        title={myEmojis.has(emoji) ? 'Убрать свою реакцию' : 'Поставить такую же реакцию'}
                    >
                        {actionLoading === emoji ? '⏳' : emoji} {byEmoji[emoji]}
                    </button>
                ))}

                {/* Кнопка добавления — видна только авторизованным */}
                {currentUser ? (
                    <button
                        type="button"
                        className="reaction-add-btn"
                        onClick={() => setShowPicker(!showPicker)}
                        disabled={actionLoading !== null}
                        title="Добавить реакцию"
                    >
                        {showPicker ? '✕' : '+ 😊'}
                    </button>
                ) : (
                    <span className="track-reactions__empty" title="Войдите, чтобы реагировать">
                        Войдите, чтобы реагировать
                    </span>
                )}
            </div>

            {/* Пикер эмодзи */}
            {showPicker && currentUser && (
                <div className="track-reactions__picker">
                    {QUICK_EMOJIS.map((emoji) => (
                        <button
                            key={emoji}
                            type="button"
                            className={`reaction-picker-item${myEmojis.has(emoji) ? ' reaction-picker-item--mine' : ''}`}
                            onClick={() => handleToggle(emoji)}
                            disabled={actionLoading !== null}
                            title={myEmojis.has(emoji) ? 'Убрать реакцию' : 'Поставить реакцию'}
                        >
                            {actionLoading === emoji ? '⏳' : emoji}
                        </button>
                    ))}
                </div>
            )}

            {!compact && totalCount > 0 && (
                <div className="track-reactions__total">
                    Всего: {totalCount}
                </div>
            )}
        </div>
    );
}
